import { useEffect, useState } from 'react';

export function formatRelativeTime(
  value: string | Date,
  locale: string,
): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '';

  const now = Date.now();
  const diffSec = Math.round((date.getTime() - now) / 1000);
  const absSec = Math.abs(diffSec);
  const rtf = new Intl.RelativeTimeFormat(locale === 'de' ? 'de' : 'en', {
    numeric: 'auto',
  });

  if (absSec < 60) return rtf.format(diffSec, 'second');
  const diffMin = Math.round(diffSec / 60);
  if (Math.abs(diffMin) < 60) return rtf.format(diffMin, 'minute');
  const diffHour = Math.round(diffSec / 3600);
  if (Math.abs(diffHour) < 24) return rtf.format(diffHour, 'hour');
  const diffDay = Math.round(diffSec / 86400);
  if (Math.abs(diffDay) < 30) return rtf.format(diffDay, 'day');
  const diffMonth = Math.round(diffSec / (86400 * 30));
  if (Math.abs(diffMonth) < 12) return rtf.format(diffMonth, 'month');
  const diffYear = Math.round(diffSec / (86400 * 365));
  return rtf.format(diffYear, 'year');
}

/** Re-computes a relative time label on an interval so "5 minutes ago" stays fresh. */
export function useRelativeTime(
  value: string | Date | null | undefined,
  locale: string,
  intervalMs = 60_000,
): string {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (value == null) return;
    const id = window.setInterval(() => setTick((n) => n + 1), intervalMs);
    return () => window.clearInterval(id);
  }, [value, intervalMs]);

  if (value == null) return '';
  return formatRelativeTime(value, locale);
}
